/**
 * ReadNextSlider.tsx
 *
 * Embla Carousel-based "Continue Reading" slider for blog post pages.
 * Appears at the bottom of every blog post page, below article+sidebar columns.
 *
 * SSR requirement: all anchor tags are present in SSR'd HTML (no mounted gate).
 * The card grid is static HTML at render time. Embla is initialised lazily —
 * only after the section enters the viewport (IntersectionObserver). This keeps
 * the Embla JS off the critical path and avoids TBT regression on blog post pages.
 *
 * Breakpoints (slidesToShow):
 *   ≤ 600px   → 1 card  (CSS flex basis)
 *   601–1023px → 2 cards (CSS flex basis)
 *   ≥ 1024px  → 3 cards (CSS flex basis)
 *
 * All CSS lives in globals.css under .blog-readnext-section scope.
 *
 * Autoplay (added 2026-05-07):
 *   - embla-carousel-autoplay plugin, 5000ms cadence.
 *   - stopOnMouseEnter: true — hover pauses; mouse-leave resumes.
 *   - stopOnInteraction: false — arrow/swipe pauses briefly then resumes.
 *   - Keyboard focus inside the slider pauses autoplay; blur resumes.
 *   - prefers-reduced-motion: reduce disables autoplay entirely (WCAG 2.2 SC 2.2.2).
 *     A matchMedia 'change' listener handles runtime OS-setting changes.
 *   - loop: true so the carousel wraps continuously.
 *   - No visual autoplay indicator (no progress bar or dots).
 *
 * Author: Vela — 2026-05-07 (READ-NEXT + PRO BLOCK + NEWSLETTER brief)
 * Revised: deferred Embla init via IntersectionObserver to prevent TBT regression.
 * Revised: autoplay plugin wired (continuation cycle 2026-05-07).
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { SliderPost } from '@/lib/content-types';
import { formatReadingTime } from '@/lib/reading-time-format';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const categoryLabels: Record<string, string> = {
  technique: 'Technique',
  musicality: 'Musicality',
  practice: 'Practice',
  repertoire: 'Repertoire',
  mindset: 'Mindset',
  tips: 'Tips & Tricks',
  theory: 'Music Theory',
  'inspiration-music': 'Inspiration & Music',
  'piano-tutorials': 'Piano Tutorials',
  'piano-fantasy-minute': 'Piano Fantasy Minute',
};

function formatCategory(cat: string | null): string {
  if (!cat) return 'Article';
  return categoryLabels[cat] ?? cat.charAt(0).toUpperCase() + cat.slice(1);
}

// Root cause of the 2026-07-09 React hydration error #418 on blog posts.
//
// Diagnosed via `next dev` (non-minified React) with the SSR server pinned to
// TZ=UTC (matching the real Cloudflare Workers runtime) and the browser
// context forced to a negative-offset timezone (America/Los_Angeles), which
// deterministically reproduces the exact cross-timezone condition a real
// visitor creates. The FULL (non-minified) hydration warning pointed at THIS
// component's rendered date text specifically:
//   + March 24, 2024   (client)
//   - March 25, 2024   (server)
// for one of the related-post cards this component renders.
//
// Cause: `toLocaleDateString('en-US', {...})` with no `timeZone` option
// formats in the RUNTIME's local timezone. On the server that's UTC
// (Cloudflare Workers); on the client it's whatever timezone the visitor's
// browser is in. A publishDate stored as a bare `YYYY-MM-DD` parses to
// midnight UTC — for a visitor in any negative-UTC-offset timezone (all of
// the Americas), midnight UTC is still the PREVIOUS calendar day locally,
// so the client renders a different date than the server did. That is a
// real, byte-different text node — a genuine React hydration mismatch, not
// just console noise — and worse, it means some visitors were shown the
// wrong publish date on related-post cards.
//
// `timeZone: 'UTC'` pins formatting to the stored calendar date regardless
// of the runtime's or visitor's timezone, so server and client always agree.
// Same fix applied to the four sibling copies of this exact function:
// app/page.tsx, app/blog/_components/FeaturedPostHero.tsx,
// app/blog/_client/BlogIndexClient.tsx, and
// app/blog/[slug]/_client/BlogPostPageClient.tsx (which also had a second,
// independent instance of the same root cause — a raw Date object passed
// directly into a `dateTime` attribute — fixed separately in that file).
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return '';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

const fallbackImg = '/images/piano-keys-cover.jpg';

// ---------------------------------------------------------------------------
// Icon primitives — inline SVG (no external dep)
// ---------------------------------------------------------------------------

const ChevronRight = ({ size = 13 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="9 6 15 12 9 18" />
  </svg>
);

const ArrowLeft = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="19" y1="12" x2="5" y2="12" />
    <polyline points="11 6 5 12 11 18" />
  </svg>
);

const ArrowRight = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="13 6 19 12 13 18" />
  </svg>
);

// ---------------------------------------------------------------------------
// ReadNextSlider component
// Deferred Embla init: IntersectionObserver fires when the section nears viewport,
// then dynamically imports Embla and attaches to the pre-rendered track.
// SSR HTML is intact (all 8 card anchors present) regardless of JS state.
// ---------------------------------------------------------------------------

interface ReadNextSliderProps {
  posts: SliderPost[];
}

export default function ReadNextSlider({ posts }: ReadNextSliderProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const prevBtnRef = useRef<HTMLButtonElement>(null);
  const nextBtnRef = useRef<HTMLButtonElement>(null);

  // emblaApi stored in a ref — updating it doesn't need a re-render
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emblaApiRef = useRef<any>(null);
  // autoplay plugin instance — stored in a ref so we can call play()/stop() directly
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const autoplayRef = useRef<any>(null);
  const [emblaReady, setEmblaReady] = useState(false);

  const updateButtonStates = useCallback(() => {
    const api = emblaApiRef.current;
    if (!api || !prevBtnRef.current || !nextBtnRef.current) return;
    // With loop: true, both directions are always scrollable once Embla is ready.
    // Keep the standard canScroll checks — Embla returns true for both when looping.
    prevBtnRef.current.disabled = !api.canScrollPrev();
    nextBtnRef.current.disabled = !api.canScrollNext();
  }, []);

  const scrollPrev = useCallback(() => emblaApiRef.current?.scrollPrev(), []);
  const scrollNext = useCallback(() => emblaApiRef.current?.scrollNext(), []);

  // Deferred Embla init — fires when the section enters 200px below the viewport
  useEffect(() => {
    const section = sectionRef.current;
    const viewport = viewportRef.current;
    if (!section || !viewport) return;

    // Reduced-motion check — evaluated once per init and again on OS-setting changes.
    // WCAG 2.2 SC 2.2.2: if the user prefers reduced motion, autoplay is disabled entirely.
    const reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
    let reducedMotion = reducedMotionMQ.matches;

    const initEmbla = async () => {
      // Dynamic imports — neither Embla nor the autoplay plugin is in the initial bundle.
      const [{ default: EmblaCarousel }, { default: Autoplay }] = await Promise.all([
        import('embla-carousel'),
        import('embla-carousel-autoplay'),
      ]);

      // Build plugin list: autoplay is included only when reduced-motion is off.
      const autoplayPlugin = reducedMotion
        ? null
        : Autoplay({
            delay: 5000,
            stopOnMouseEnter: true,    // hover pauses; mouse-leave resumes
            stopOnInteraction: false,  // arrow/swipe briefly pauses then resumes
          });

      const plugins = autoplayPlugin ? [autoplayPlugin] : [];

      const api = EmblaCarousel(
        viewport,
        {
          align: 'start',
          slidesToScroll: 1,
          loop: true,   // continuous wrap — autoplay rolls without bouncing at the end
          dragFree: false,
        },
        plugins,
      );

      emblaApiRef.current = api;
      autoplayRef.current = autoplayPlugin;

      updateButtonStates();
      api.on('select', updateButtonStates);
      api.on('reInit', updateButtonStates);
      setEmblaReady(true);

      // Keyboard focus/blur listeners — pause autoplay when any focusable element
      // inside the slider has keyboard focus; resume on blur.
      // Uses focusin/focusout (bubble) so a single listener covers all descendants.
      if (autoplayPlugin) {
        const handleFocusIn = () => autoplayPlugin.stop();
        const handleFocusOut = () => {
          // Only resume if focus has truly left the viewport (relatedTarget outside)
          if (!viewport.contains(document.activeElement)) {
            autoplayPlugin.play();
          }
        };
        viewport.addEventListener('focusin', handleFocusIn);
        viewport.addEventListener('focusout', handleFocusOut);
        // Store refs for cleanup
        (viewport as HTMLDivElement & { _autoplayHandlers?: { focusin: () => void; focusout: () => void } })
          ._autoplayHandlers = { focusin: handleFocusIn, focusout: handleFocusOut };
      }
    };

    // Runtime OS-setting change: if user toggles reduced-motion while on the page,
    // stop autoplay immediately (we can't re-init Embla mid-session without a DOM
    // teardown, so we just stop it — the slider stays functional as a manual carousel).
    const handleReducedMotionChange = (e: MediaQueryListEvent) => {
      reducedMotion = e.matches;
      if (reducedMotion && autoplayRef.current) {
        autoplayRef.current.stop();
      }
      // Note: if reduced-motion is turned OFF mid-session, we do NOT auto-restart
      // autoplay (would require re-init). The page reload will pick up the new setting.
    };
    reducedMotionMQ.addEventListener('change', handleReducedMotionChange);

    if (!('IntersectionObserver' in window)) {
      // Fallback: init immediately if IO not available
      initEmbla();
      return () => {
        reducedMotionMQ.removeEventListener('change', handleReducedMotionChange);
      };
    }

    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          io.disconnect();
          initEmbla();
        }
      },
      { rootMargin: '200px 0px' },
    );
    io.observe(section);

    return () => {
      io.disconnect();
      reducedMotionMQ.removeEventListener('change', handleReducedMotionChange);
      // Clean up focus listeners
      const vp = viewportRef.current as (HTMLDivElement & { _autoplayHandlers?: { focusin: () => void; focusout: () => void } }) | null;
      if (vp?._autoplayHandlers) {
        vp.removeEventListener('focusin', vp._autoplayHandlers.focusin);
        vp.removeEventListener('focusout', vp._autoplayHandlers.focusout);
        delete vp._autoplayHandlers;
      }
      // Clean up Embla on unmount (destroys the autoplay plugin too)
      if (emblaApiRef.current) {
        emblaApiRef.current.off('select', updateButtonStates);
        emblaApiRef.current.off('reInit', updateButtonStates);
        emblaApiRef.current.destroy();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!posts.length) return null;

  return (
    <section
      ref={sectionRef}
      className="blog-readnext-section"
      aria-label="Continue reading"
    >
      <div className="blog-readnext-section__inner">
        <div className="blog-readnext-section__header">
          <h2 className="blog-readnext-section__title">Continue Reading</h2>
          <div
            className="blog-readnext-section__controls"
            role="group"
            aria-label="Slider controls"
          >
            <button
              ref={prevBtnRef}
              className="blog-readnext-section__arrow blog-readnext-section__arrow--prev"
              type="button"
              aria-label="Previous posts"
              onClick={scrollPrev}
              // Disabled until Embla is ready; re-enabled with correct canScrollPrev state
              disabled={!emblaReady}
            >
              <ArrowLeft size={20} />
            </button>
            <button
              ref={nextBtnRef}
              className="blog-readnext-section__arrow blog-readnext-section__arrow--next"
              type="button"
              aria-label="Next posts"
              onClick={scrollNext}
              disabled={!emblaReady}
            >
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/*
          Embla viewport: overflow-hidden is applied via CSS (.blog-readnext-section__viewport).
          Before Embla attaches, the track renders as a horizontal flex row (scrollable on mobile
          via CSS overflow-x: auto — graceful no-JS fallback).
        */}
        <div
          className={`blog-readnext-section__viewport${emblaReady ? ' is-embla-ready' : ''}`}
          ref={viewportRef}
        >
          <div className="blog-readnext-section__track">
            {posts.map((post) => (
              <div className="blog-readnext-section__slide" key={post.slug}>
                {/* SSR-rendered anchor — crawlable by Google without JS */}
                <a
                  href={`/blog/${post.slug}`}
                  className="post-card"
                  aria-label={`Read: ${post.title}`}
                >
                  <div className="post-img-wrap">
                    <span className="post-tag">{formatCategory(post.category ?? null)}</span>
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    {/* Cycle 82: class="post-slider__img" — explicit max-width constraint
                        in addition to .post-img-wrap img descendant rule. Belt-and-suspenders
                        guard against parent class drift. */}
                    <img
                      className="post-slider__img"
                      src={post.coverImage ?? fallbackImg}
                      alt={(post.coverImageAlt ?? post.title) ?? undefined}
                      loading="lazy"
                      width={800}
                      height={600}
                    />
                  </div>
                  <div className="post-body">
                    <div className="post-meta">
                      <span>{formatDate(post.publishDate)}</span>
                      <span className="sep">&middot;</span>
                      <span>{formatReadingTime(post.readingTimeMinutes as number)} min read</span>
                    </div>
                    <h3>{post.title}</h3>
                    {post.excerpt && (
                      <p className="post-excerpt">{post.excerpt}</p>
                    )}
                    <span className="post-read">
                      Read
                      <ChevronRight size={13} />
                    </span>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
